import React from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Link as RouterLink } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

export default function PrivacyPolicy() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Container maxWidth="md" sx={{ mt: isMobile ? '80px' : '128px', mb: 6, px: isMobile ? 2 : 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        Privacy Policy
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Last updated: May 12, 2026
      </Typography>

      <Section title="1. Information We Collect">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          When you create an account on The Big Board, we collect:
        </Typography>
        <Box component="ul" sx={{ mt: 1, pl: 3, '& li': { mb: 0.5, lineHeight: 1.8 } }}>
          <li><strong>Email address</strong> — used for account authentication and identification.</li>
          <li><strong>Display name</strong> — optional, shown on leaderboards instead of your email.</li>
          <li><strong>Phone number</strong> — optional, provided only if you opt in to SMS notifications.</li>
          <li><strong>Game picks and scores</strong> — your weekly selections and predicted scores.</li>
        </Box>
      </Section>

      <Section title="2. How We Use Your Information">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          We use your information to:
        </Typography>
        <Box component="ul" sx={{ mt: 1, pl: 3, '& li': { mb: 0.5, lineHeight: 1.8 } }}>
          <li>Operate and maintain the Service (authentication, leaderboards, pick tracking).</li>
          <li>Send SMS notifications including phone verification codes, new-week game alerts, kickoff reminders, and leaderboard updates (only if you opt in).</li>
          <li>Display your picks and scores to other authenticated users on the leaderboard.</li>
        </Box>
      </Section>

      <Section title="3. SMS and Phone Number Policy">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          The <strong>BigBoard SMS Notification Program</strong> is entirely optional and is never required
          to use the Service. You opt in by entering your phone number on your Profile page, completing
          one-time-passcode (OTP) verification of that number, and explicitly enabling one or more
          notification toggles, all of which default to OFF.
        </Typography>
        <Typography variant="body1" sx={{ lineHeight: 1.8, mt: 1 }}>
          If you choose to opt in:
        </Typography>
        <Box component="ul" sx={{ mt: 1, pl: 3, '& li': { mb: 0.5, lineHeight: 1.8 } }}>
          <li>Your phone number is stored securely and used solely for sending notifications you have opted into.</li>
          <li>We do not sell, share, or rent your phone number to third parties.</li>
          <li>No mobile information or SMS opt-in consent will be shared with third parties or affiliates for marketing or promotional purposes.</li>
          <li>Message frequency varies based on your preferences and game schedules.</li>
          <li>Message and data rates may apply.</li>
          <li>You may opt out at any time by replying STOP to any message or disabling notifications in your profile settings.</li>
          <li>Reply HELP to any message for support information.</li>
          <li>Carriers are not liable for delayed or undelivered messages.</li>
        </Box>
      </Section>

      <Section title="4. Data Sharing">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          We do not sell your personal information. Your data may be visible to other authenticated users
          of The Big Board in the following ways:
        </Typography>
        <Box component="ul" sx={{ mt: 1, pl: 3, '& li': { mb: 0.5, lineHeight: 1.8 } }}>
          <li>Your display name (or email if no display name is set) appears on the leaderboard.</li>
          <li>Your weekly picks are visible to other users after game kickoff times.</li>
        </Box>
        <Typography variant="body1" sx={{ mt: 1, lineHeight: 1.8 }}>
          We use third-party services (Firebase by Google, AWS) to host and operate the Service. These
          providers have their own privacy policies governing data they process on our behalf.
        </Typography>
      </Section>

      <Section title="5. Data Security">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          We use industry-standard security measures including encrypted authentication (Firebase Auth),
          server-side access controls, and secure secret storage (Google Secret Manager) to protect your data.
        </Typography>
      </Section>

      <Section title="6. Data Retention">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          We retain your account data for as long as your account is active. You may request deletion of
          your account and associated data by contacting us.
        </Typography>
      </Section>

      <Section title="7. Your Rights">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          You may update your profile information at any time through the app. To request data export
          or account deletion, contact us at the address below.
        </Typography>
      </Section>

      <Section title="8. Children's Privacy">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          The Service is not directed to children under 13, and we do not knowingly collect personal
          information from anyone under 13. Users must be at least 18 years old to use the Service. If we
          learn that we have collected information from a child under 13, we will promptly delete it.
          Parents or guardians who believe their child has provided us with personal information may
          contact us at the email address below.
        </Typography>
      </Section>

      <Section title="9. Changes to This Policy">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          We may update this Privacy Policy from time to time. Continued use of the Service after changes
          constitutes acceptance of the revised policy.
        </Typography>
      </Section>

      <Section title="10. Contact">
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          For privacy-related questions or requests, contact us at{' '}
          <a href="mailto:support@bigboard.football" style={{ color: '#555' }}>support@bigboard.football</a>.
        </Typography>
      </Section>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
        See also our <RouterLink to="/terms" style={{ color: '#666' }}>Terms of Service</RouterLink>.
      </Typography>
    </Container>
  );
}
